import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { ScoopfreeActivity } from '@model/product/scoopfree/activity/scoopfree-activity.model';
import { ScoopfreeActivityType } from '@model/product/scoopfree/activity/scoopfree-activity-type.model';
import { Scoopfree } from '@model/product/scoopfree/scoopfree.model';
import { ChartDataSets, ChartOptions, ChartType } from 'chart.js';
import { BaseChartDirective, Color, Label } from 'ng2-charts';
import { DateUtilService } from 'src/app/services/util/date-util.service';
import { LOG_LEVEL, LoggerService } from '@app/services/util/logger.service';

/**
 * ProductActivityChartComponent
 *
 * @export ProductActivityChartComponent
 */
@Component({
  selector: 'app-product-activity-chart',
  templateUrl: './product-activity-chart.component.html',
  styleUrls: ['./product-activity-chart.component.scss'],
})
export class ProductActivityChartComponent
  implements OnInit, OnChanges {
  /**
   * Input of selected product
   */
  @Input() selectedProduct: Scoopfree;

  /**
   * Input of selected activity types for the platform
   */
  @Input() selectedActivityType: ScoopfreeActivityType;

  /**
   * member variable for selectedChartFilter
   */
  private mSelectedChartFilter = 'W';

  /**
   * Input setter for selectedChartFilter - resets the chart when changed
   */
  @Input()
  set selectedChartFilter(val: string) {
    this.mSelectedChartFilter = val;
    this.resetChart();
  }

  /**
   * selectedChartFilter getter
   */
  get selectedChartFilter() {
    return this.mSelectedChartFilter;
  }

  /**
   * Ouput EventEmitter for selectedChartFilter
   */
  @Output() selectedChartFilterChange: EventEmitter<
    string
  > = new EventEmitter<string>();

  /**
   * Input of activities to be charted
   */
  @Input() activities: Array<{
    date: string;
    activities: Array<ScoopfreeActivity>;
  }>;

  /**
   * date filter start
   */
  startDate = '2019-11-01';

  /**
   * date filter end
   */
  endDate = '2019-11-08';

  /**
   * week filter start
   */
  startWeek = 'Week 10-2019';

  /**
   * week filter end
   */
  endWeek = 'Week 12-2019';

  /**
   * month filter start
   */
  startMonth = '6 June-2019';

  /**
   * month filter end
   */
  endMonth = '7 July-2019';

  /**
   * ngCharts datasets
   */
  public lineChartData: ChartDataSets[] = [
    { data: [], label: 'Activity' },
  ];

  /**
   * ngCharts labels
   */
  public lineChartLabels: Label[] = [];

  /**
   * ngCharts chartOptions
   */
  public lineChartOptions: ChartOptions & { annotation: any } = {
    responsive: true,
    scales: {
      xAxes: [{}],
      yAxes: [
        {
          id: 'y-axis-0',
          position: 'left',
          ticks: { min: 0, stepSize: 1 },
        },
      ],
    },
    annotation: {
      annotations: [
        {
          type: 'line',
          mode: 'vertical',
          scaleID: 'x-axis-0',
          value: 'March',
          borderColor: 'orange',
          borderWidth: 2,
          label: {
            enabled: true,
            fontColor: 'orange',
            content: 'LineAnno',
          },
        },
      ],
    },
  };

  /**
   * ngCharts Chart Colors
   */
  public lineChartColors: Color[] = [
    {
      // grey
      backgroundColor: 'rgba(104, 184, 144,0.2)',
      borderColor: 'rgba(104, 184, 144,1)',
      pointBackgroundColor: 'rgba(104, 184, 144,1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(104, 184, 144,0.8)',
    },
  ];
  /**
   * ngCharts enable legend
   */
  public lineChartLegend = true;

  /**
   * ngCharts set chart type to line
   */
  public lineChartType: ChartType = 'line';

  /**
   * boolean to check if activities have been initialised
   */
  isInit = false;
  /**
   * HTML DOM element of chart
   */
  @ViewChild(BaseChartDirective, { static: true })
  chart: BaseChartDirective;

  /**
   * Creates an instance of ProductActivityChartComponent.
   */
  constructor() {}

  /**
   * Initialise the chart filters and reset the chart
   */
  ngOnInit() {
    if (this.activities !== undefined && this.activities !== null) {
      if (this.activities.length > 0) {
        this.startDate = this.activities[0].date;
        this.endDate = this.activities[
          this.activities.length - 1
        ].date;
        this.startWeek = DateUtilService.getWeekNumber(
          new Date(this.startDate),
        );
        this.endWeek = DateUtilService.getWeekNumber(
          new Date(this.endDate),
        );
        this.startMonth = DateUtilService.getMonth(
          new Date(this.startDate),
        );
        this.endMonth = DateUtilService.getMonth(
          new Date(this.endDate),
        );
        this.isInit = true;
        this.resetChart();
      }
    }
  }

  /**
   * If activities change, update chartFilters and reset the chart
   */
  ngOnChanges() {
    if (this.activities) {
      if (!this.isInit) {
        if (this.activities.length > 0) {
          this.startDate = this.activities[0].date;
          this.endDate = this.activities[
            this.activities.length - 1
          ].date;
          this.startWeek = DateUtilService.getWeekNumber(
            new Date(this.startDate),
          );
          this.endWeek = DateUtilService.getWeekNumber(
            new Date(this.endDate),
          );
          this.startMonth = DateUtilService.getMonth(
            new Date(this.startDate),
          );
          this.endMonth = DateUtilService.getMonth(
            new Date(this.endDate),
          );
          this.isInit = true;
          this.resetChart();
        }
      }
      this.updateChartFilters(this.selectedChartFilter);
      this.resetChart();
    }
  }

  /**
   * select activity type and reset chart
   */
  selectActivityType(): void {
    this.selectedChartFilterChange.emit(this.selectedChartFilter);
    this.resetChart();
  }

  /**
   * reset chart values using current activites and filters
   *
   * @returns void
   */
  resetChart(): void {
    if (this.activities) {
      if (this.activities.length > 0) {
        switch (this.selectedChartFilter) {
          case 'D':
            this.lineChartLabels = DateUtilService.getDates(
              this.startDate,
              this.endDate,
            );
            break;
          case 'W':
            this.lineChartLabels = DateUtilService.getWeeks(
              parseInt(
                this.startWeek.split(' ')[1].split('-')[0],
                10,
              ),
              parseInt(
                this.startWeek.split(' ')[1].split('-')[1],
                10,
              ),
              parseInt(this.endWeek.split(' ')[1].split('-')[0], 10),
              parseInt(this.endWeek.split(' ')[1].split('-')[1], 10),
            );
            break;
          case 'M':
            this.lineChartLabels = DateUtilService.getMonths(
              this.startMonth,
              this.endMonth,
            );
            break;
        }

        this.lineChartData = new Array<ChartDataSets>();
        // Get list of unique ActivityTypes
        for (const aList of this.activities) {
          for (const a of aList.activities) {
            if (
              !this.lineChartData.find(lcd => lcd.label === a.code)
            ) {
              if (a.code != null) {
                this.lineChartData.push({ label: a.code, data: [] });
              }
            }
          }
        }

        for (const lineChartLabel of this.lineChartLabels) {
          const activitiesOnThisDate: {
            date: string;
            activities: ScoopfreeActivity[];
          } = this.activities.find(
            act => act.date === lineChartLabel,
          );
          if (activitiesOnThisDate) {
            const allActivities = activitiesOnThisDate.activities;

            const groups = allActivities.reduce((g, activity) => {
              const code = activity.code;
              if (!g[code]) {
                g[code] = [];
              }
              g[code].push(activity);
              return g;
            }, {});
            const groupArray = Object.keys(groups).map(code => {
              return {
                label: code,
                data: groups[code].length,
              };
            });
            for (const lcd of this.lineChartData) {
              const x: { label: string; data: any } = groupArray.find(
                ga => ga.label === lcd.label,
              );
              if (x) {
                lcd.data.push(x.data);
              } else {
                lcd.data.push(0);
              }
            }
          } else {
            for (const lcd of this.lineChartData) {
              lcd.data.push(0);
            }
          }
        }

        // const newData: ChartDataSets = this.lineChartData[0];
        // const data: Array<number> = [];
        // for (const lineChartLabel of this.lineChartLabels) {
        //   const x: { date: string; activities: Activity[]; } = this.activities.find(act => act.date === lineChartLabel);
        //   if (x) {
        //     data.push(x.activities.length);
        //   } else {
        //     data.push(0);
        //   }
        // }
        // newData.data = data;
        // this.lineChartData[0] = newData;
        // if (this.selectedActivityType !== ActivityTypes.SHOW_ALL) {
        //   this.lineChartData[0].label = this.selectedActivityType.toString();
        // } else {
        //   this.lineChartData[0].label = 'All Activity';
        // }

        return;
      }
    }
    this.lineChartData[0].data = [];
  }

  /**
   * log that chart was clicked
   *
   * @todo add functionality to chart click
   *
   * @param event the chart event triggered on mouse click
   */
  chartClicked(event: any): void {
    if (event.active[0]) {
      LoggerService.log(this.lineChartLabels[event.active[0]._index], LOG_LEVEL.DEBUG);
    }
  }

  /**
   * called when a user hovers mouse over the chart
   */
  chartHovered(event: any): void {
    // todo
  }

  /**
   * getWeekGreaterThan
   *
   * @param sW start week string ex 'Week 23-year'
   * @param eW end week string ex 'Week 45-year'
   * @returns true if greater
   */
  getWeekGreaterThan(sW: string, eW: string): boolean {
    const sWnY: number = parseInt(sW.split(' ')[1].split('-')[1], 10);
    const eWnY: number = parseInt(eW.split(' ')[1].split('-')[1], 10);
    const sWn: number = parseInt(sW.split(' ')[1].split('-')[0], 10);
    const eWn: number = parseInt(eW.split(' ')[1].split('-')[0], 10);
    if (sWnY === eWnY) {
      return sWn > eWn;
    } else {
      return sWnY > eWnY;
    }
  }

  /**
   * getMonthGreaterThan
   *
   * @param sW start month string ex '6 June-year'
   * @param eW end month string ex '8 August-year'
   * @returns true if greater
   */
  getMonthGreaterThan(sM: string, eM: string): boolean {
    const sMnY: number = parseInt(sM.split(' ')[1].split('-')[1], 10);
    const eMnY: number = parseInt(eM.split(' ')[1].split('-')[1], 10);
    const sMn: number = parseInt(sM.split(' ')[0], 10);
    const eMn: number = parseInt(eM.split(' ')[0], 10);
    if (sMnY === eMnY) {
      return sMn > eMn;
    } else {
      return sMnY > eMnY;
    }
  }

  /**
   * Updates Date, Week, and Month start and end  filters to match each other
   *
   * @param chartFilter current chart filter
   */
  updateChartFilters(chartFilter: string): void {
    switch (chartFilter) {
      case 'D':
        this.startWeek = DateUtilService.getWeekNumber(
          new Date(this.startDate),
        );
        this.endWeek = DateUtilService.getWeekNumber(
          new Date(this.endDate),
        );
        this.startMonth = DateUtilService.getMonth(
          new Date(this.startDate),
        );
        this.endMonth = DateUtilService.getMonth(
          new Date(this.endDate),
        );
        break;
      case 'W':
        this.startDate = DateUtilService.toFormatedString(
          DateUtilService.getDateOfWeek(
            parseInt(this.startWeek.split(' ')[1].split('-')[0], 10),
            parseInt(this.startWeek.split(' ')[1].split('-')[1], 10),
          ),
        );
        this.endDate = DateUtilService.toFormatedString(
          DateUtilService.getDateOfWeek(
            parseInt(this.endWeek.split(' ')[1].split('-')[0], 10),
            parseInt(this.endWeek.split(' ')[1].split('-')[1], 10),
          ),
        );
        this.startMonth = DateUtilService.getMonth(
          new Date(this.startDate),
        );
        this.endMonth = DateUtilService.getMonth(
          new Date(this.endDate),
        );
        break;
      case 'M':
        // get earliest date in start month and set start date to this
        this.startDate = DateUtilService.toFormatedString(
          new Date(
            this.startMonth.split(' ')[1].split('-')[1] +
              '-' +
              this.startMonth.split(' ')[0] +
              '-01',
          ),
        );
        // get latest date in end month and set end date to this
        this.endDate = DateUtilService.toFormatedString(
          new Date(
            this.endMonth.split(' ')[1].split('-')[1] +
              '-' +
              this.endMonth.split(' ')[0] +
              '-01',
          ),
        );
        this.startWeek = DateUtilService.getWeekNumber(
          new Date(this.startDate),
        );
        this.endWeek = DateUtilService.getWeekNumber(
          new Date(this.endDate),
        );
    }
    this.resetChart();
  }
}
